import { createContext, useContext, useState, type ReactNode } from "react";
import { useT } from "../i18n";
import { useRuf } from "../schale/Ruf";
import { useDarfSchreiben } from "../schale/Sitzung";
import { useAbholungSchalten, usePositionAnlegen } from "../daten/haken";
import { positionsFehlerPfad } from "./Positionen";
import type { Auftrag } from "../daten/routen";
import type { Position } from "../daten/typen";
import { FehlerBand, Knopf } from "./index";

/* ══════════════════════════════════════════════════════════════════
   Liefern wir, oder holt der Kunde? — am Vorgang, auch nach der Annahme.

   Lisa, Systemprüfung B-04 (23.09.2026): *„Wechsel Anlieferung ↔ Abholung
   nach Annahme nicht möglich."* Den Schalter „Kunde holt ab" gab es nur in
   der Merkliste — also nur, bevor ein Vorgang entstand. Sagte der Kunde nach
   der Zusage „wir holen doch selbst", stand die Anlieferung weiter am
   Auftrag, und kein Handgriff nahm sie weg.

   **Ein Haken an der Anlieferungszeile** (Systemprüfung, Befund 17, Lucas
   Entscheidung (b) vom 24.09.2026). Bis dahin standen unter der Liste zwei
   Knöpfe, „Wir liefern an" und „Kunde holt ab"; Lisa fand das unhandlich und
   wollte, wie beim Vermieten, einen Haken an der Zeile. Er sitzt jetzt an der
   Zeile, die er wegnimmt — an der Anlieferung, solange sie gilt, und an der
   Zeile „Selbstabholung" (0,00), sobald sie abgewählt ist. **Er gilt
   weiterhin für den ganzen Vorgang**: Die Anlieferung rechnet einmal je
   Auftrag, und ein Haken je Gerät wäre eine andere Regel (Option c, nicht
   gewählt).

   **Kein neuer Preis.** Der Haken legt die Zeile an, mit der auch die
   Merkliste die Anlieferung abwählt (0,00), oder nimmt sie wieder weg; es
   gilt dann wieder der Satz, der bei der Annahme eingefroren wurde. Einen
   Anhänger legt er hier nicht dazu — der ist eine eigene Position mit
   eigenem Preis und kommt wie jede andere dazu.

   **Warum ein Rahmen.** Die Anlieferung steht in drei Listen (Tabelle,
   Karten am Telefon, Leseliste), und keine davon kennt den Vorgang. Der
   Rahmen um die Liste sagt dem Haken, zu welchem Vorgang er gehört; ohne
   Rahmen zeichnet er nichts.
   ══════════════════════════════════════════════════════════════════ */

type Lieferstand = { nr: string; abholung: Auftrag["abholung"] };

const LieferwegKontext = createContext<Lieferstand | null>(null);

/** Um eine Positionsliste: Wer darin eine Anlieferung findet, bekommt den Haken. */
export function Lieferweg({ nr, abholung, children }: Lieferstand & { children: ReactNode }) {
  return <LieferwegKontext.Provider value={{ nr, abholung }}>{children}</LieferwegKontext.Provider>;
}

/**
 * Der Haken „Kunde holt ab" — an einer Zuschlagszeile aus der Regel (`code`)
 * oder an einer gespeicherten Zeile (`zeile`). Er erscheint nur, wo er etwas
 * bewegt: an einer Regel, die bei Selbstabholung entfällt, oder an der
 * abgewählten Anlieferung selbst.
 */
export function AbholHaken({ code, zeile }: { code?: string; zeile?: Position }) {
  const t = useT();
  const ruf = useRuf();
  const stand = useContext(LieferwegKontext);
  const schalten = useAbholungSchalten();
  const anlegen = usePositionAnlegen();
  /* Wer nur liest (P7-03), sieht den Haken, legt ihn aber nicht um. */
  const schreibt = useDarfSchreiben();
  /* „Ohne Anhänger" gilt für diesen Blick auf den Vorgang — kommt man wieder,
     fragt die Zeile noch einmal. Ein Nein am Telefon ist kein Stammdatum. */
  const [ohneAnhaenger, setzeOhneAnhaenger] = useState(false);
  const abholung = stand?.abholung;
  if (!stand || !abholung?.moeglich) return null;

  const an = zeile !== undefined;
  const passt = an ? zeile.abholzeile === true : code !== undefined && abholung.codes.includes(code);
  if (!passt) return null;

  const gesperrt = !abholung.schaltbar || schalten.isPending || !schreibt;
  const umlegen = (neu: boolean) => {
    if (neu === abholung.an) return;
    if (neu) setzeOhneAnhaenger(false);
    schalten.mutate({ nr: stand.nr, an: neu }, {
      onSuccess: ({ ohneBediener }) => {
        /* **Holt der Kunde ab, fährt keiner von uns mit** (Paket 9, Runde 4):
           Stand an einer Miete „mit Bediener", hat der Schalter das
           aufgehoben — das wird gesagt, mit Namen, und bleibt stehen, bis es
           gesehen ist. Nicht still. */
        if (neu && ohneBediener.length > 0) {
          ruf("abholungOhneBediener", { was: ohneBediener.join(", ") }, "acht", { bleibt: true });
        } else {
          ruf(neu ? "abholungAn" : "abholungAus");
        }
      },
    });
  };

  /* ── Und der Anhänger wird angeboten (Systemprüfung Befund 32) ──────
     Lisa, 25.09.2026: *„Beim Setzen von ‚Kunde holt ab' aktiv einen
     passenden Anhänger vorschlagen (z. B. ‚Anhänger für den Transport
     mitnehmen?')"* — in ca. 99 % der Fälle nimmt der Kunde ihn mit. In der
     Merkliste kommt er seit B8 von selbst; hier stand er nirgends.

     **Gefragt, nicht angelegt:** Am Vorgang ist der Anhänger eine neue
     Mietposition mit einem Satz von heute (Regel 8, und nach der Annahme
     Regel 4) — die entsteht nicht als Nebenwirkung eines Hakens. Also steht
     die Frage unter dem Haken, sobald er an ist, mit dem Anhänger, den die
     Gerätegruppe nennt (0127), und ein Klick legt ihn an: derselbe Typ,
     dieselbe Ausstattung, der Zeitraum der Mieten, die ihn nennen. Steht er
     schon als Miete da, fragt niemand. */
  const vorschlag = schreibt && abholung.an && !ohneAnhaenger && abholung.schaltbar && abholung.anhaenger
    && !abholung.anhaenger.da ? abholung.anhaenger : null;
  /* Der Name trägt die Ausstattung — wie in der Merkliste (`anhaengerWunsch`):
     Allein gemietet kostet derselbe Anhänger etwas anderes. */
  const anhaengerName = vorschlag
    ? (vorschlag.variante ? `${vorschlag.typ} ${t("allgemein.trennpunkt")} ${vorschlag.variante}` : vorschlag.typ)
    : "";
  const anhaengerNehmen = () => {
    if (!vorschlag) return;
    anlegen.mutate({
      nr: stand.nr,
      art: "miete",
      maschinentyp_id: vorschlag.typId,
      ...(vorschlag.varianteId ? { variante_id: vorschlag.varianteId } : {}),
      bezeichnung: anhaengerName,
      von: vorschlag.von,
      bis: vorschlag.bis,
    }, { onSuccess: () => ruf("anhaengerDazu", { was: anhaengerName }) });
  };

  return (
    <span className="lieferweg-haken">
      <label className="hakenzeile" title={abholung.schaltbar ? t("lieferweg.hakenHinweis") : t("lieferweg.gesperrt")}>
        <input
          type="checkbox"
          checked={abholung.an}
          disabled={gesperrt}
          onChange={(ev) => umlegen(ev.target.checked)}
          /* Die Zeile ist anklickbar (sie öffnet ihr Blatt) — der Haken nicht. */
          onClick={(ev) => ev.stopPropagation()}
        />
        {t("lieferweg.abholen")}
      </label>
      {vorschlag ? (
        <span className="lieferweg-anhaenger">
          <span>{t("lieferweg.anhaengerFrage", { was: anhaengerName })}</span>
          <span className="lieferweg-anhaenger-knoepfe">
            <Knopf
              klein
              art="haupt"
              disabled={anlegen.isPending}
              onClick={(ev) => { ev.stopPropagation(); anhaengerNehmen(); }}
            >
              {t("lieferweg.anhaengerJa")}
            </Knopf>
            <Knopf klein art="stumm" onClick={(ev) => { ev.stopPropagation(); setzeOhneAnhaenger(true); }}>
              {t("lieferweg.anhaengerNein")}
            </Knopf>
          </span>
        </span>
      ) : null}
      {schalten.isError ? <FehlerBand pfad={positionsFehlerPfad(schalten.error)} fehler={schalten.error} /> : null}
      {anlegen.isError ? <FehlerBand pfad={positionsFehlerPfad(anlegen.error)} fehler={anlegen.error} /> : null}
    </span>
  );
}
